// src/components/Checklist.js

import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';


import {
    fetchChecklists,
    addChecklistItem,
    deleteChecklistItem,
    deleteChecklist,
    localDeleteChecklist,
    localAddItem,
    localUpdateItem,
    localDeleteItem,
    localNewChecklist,
    localReorderItems,
    setVisibleAddNew,
    markItemToggling,
    clearItemToggling
} from '../../redux/Slices/checklistSlice';

import { addActivityOptimistically } from '../../redux/Slices/activitiesSlice';

import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';

// Pragmatic DND imports
import {
    draggable,
    dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";


import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
    attachClosestEdge,
    extractClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";

import './scss/Checklists.scss';
import Checkbox from '../Global/Checkbox';
import { config } from '../../config';
import Spinner from '../Global/Spinner';
import { marked } from 'marked';
import Checklist from '../icons/Checklist';
import Delete from '../icons/Delete';
import MoreHoriz from '../icons/MoreHoriz';
import DragIndicator from '../icons/DragIndicator';
import Close from '../icons/Close';

// Draggable Checklist Item Component
// Updated DraggableChecklistItem component with fixed drop zones

const DraggableChecklistItem = ({
    item,
    checklistId,
    index,
    editingItemId,
    editItemTitle,
    isEditing,
    isTextareaFocused,
    handleCheck,
    handleItemMore,
    setEditingItemId,
    setEditItemTitle,
    setIsEditing,
    setIsTextareaFocused,
    updateItemTitle,
    checklistItemRef,
    onReorder,
    isReadOnly = false
}) => {
    const itemRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isDropTarget, setIsDropTarget] = useState(false);
    const [closestEdge, setClosestEdge] = useState(null);

    useEffect(() => {
        const element = itemRef.current;
        if (!element) return;

        // Skip drag-and-drop setup if read-only
        if (isReadOnly) return;

        const combined = combine(
            // Draggable configuration
            draggable({
                element,
                canDrag: ({ input }) => {
                    // Prevent drag if clicking on interactive elements
                    const target = input?.event?.target;
                    if (!target) return true;
                    const isInteractiveElement = target.closest('input, button, textarea, a, [data-popover-trigger]');
                    return !isInteractiveElement;
                },
                getInitialData: () => ({
                    type: 'CHECKLIST_ITEM',
                    itemId: item._id,
                    checklistId,
                    index
                }),
                onDragStart: () => setIsDragging(true),
                onDrop: () => {
                    setIsDragging(false);
                    setIsDropTarget(false);
                    setClosestEdge(null);
                },
            }),

            // Drop target configuration
            dropTargetForElements({
                element,
                getData: ({ input, element }) => {
                    const data = {
                        type: 'CHECKLIST_ITEM',
                        itemId: item._id,
                        checklistId,
                        index
                    };
                    return attachClosestEdge(data, {
                        input,
                        element,
                        allowedEdges: ["top", "bottom"],
                        threshold: 0.5
                    });
                },
                canDrop: ({ source }) => {
                    return source.data.type === 'CHECKLIST_ITEM' &&
                        source.data.checklistId === checklistId;
                },
                onDrag: (args) => {
                    const { source, self } = args;

                    if (!self || !source) return;

                    // Skip if dragging the same item
                    if (self.data.itemId === source.data.itemId) {
                        setIsDragging(true);
                        return;
                    }

                    const edge = extractClosestEdge(self.data);
                    setClosestEdge(edge);
                    setIsDropTarget(true);
                },
                onDragLeave: () => {
                    setIsDropTarget(false);
                    setClosestEdge(null);
                },
                onDrop: (args) => {
                    const { source, self } = args;

                    if (
                        source.data.type !== 'CHECKLIST_ITEM' ||
                        source.data.checklistId !== checklistId
                    ) {
                        return;
                    }

                    const sourceIndex = source.data.index;
                    const targetIndex = self.data.index;
                    const edge = extractClosestEdge(self.data);

                    let destinationIndex = targetIndex;

                    if (edge === 'bottom') {
                        destinationIndex = targetIndex + 1;
                    }

                    // Correct shift: if dragging from below, and inserting before — shift destination back
                    if (sourceIndex < destinationIndex) {
                        destinationIndex -= 1;
                    }

                    if (sourceIndex !== destinationIndex) {
                        onReorder(checklistId, sourceIndex, destinationIndex);
                    }

                    setIsDropTarget(false);
                    setClosestEdge(null);
                }
            })
        );

        return combined;
    }, [item._id, checklistId, index, onReorder]);

    return (
        <div
            ref={itemRef}
            className={`checklist-item-wrapper ${isDragging ? 'dragging' : ''} ${isDropTarget ? 'drop-target' : ''}`}
            style={{ position: 'relative' }}
        >
            {/* Drop indicator - consistent positioning like workspace sidebar */}
            {isDropTarget && closestEdge && (
                <div
                    style={{
                        position: 'absolute',
                        left: '30px',
                        right: 0,
                        height: '2px',
                        width: 'calc(100% - 30px)',
                        backgroundColor: 'var(--brand-color)',
                        zIndex: 1000,
                        // Fix: Use consistent positioning for adjacent drop zones
                        top: closestEdge === 'top' ? '-1px' : 'auto',
                        bottom: closestEdge === 'bottom' ? '-1px' : 'auto',
                        borderRadius: '50px',
                        transition: 'all 0.2s ease',
                    }}
                />
            )}

            {!isReadOnly && (
                <div className="drag-indicator">
                    <DragIndicator size={18} />
                </div>
            )}
            <div className={`checklist-item ${(isEditing && editingItemId === item._id) ? 'currently-editing' : ''} ${item.checked ? 'checked' : ''}`}>
                <Checkbox
                    checked={item.checked}
                    id={`item-${item._id}`}
                    onChange={() => !isReadOnly && handleCheck(checklistId, item._id, item.title)}
                    disabled={isReadOnly}
                />

                {editingItemId !== item._id ? (
                    <div className='checklist-label'>
                        <label
                            onClick={(e) => {
                                if (isReadOnly) return;
                                const target = e.target;
                                if (target.tagName.toLowerCase() === 'a') {
                                    e.preventDefault();
                                    window.open(target.href, '_blank');
                                    return;
                                }
                                setEditingItemId(item._id);
                                setEditItemTitle(item.title);
                                setIsEditing(true);
                            }}
                            style={{ cursor: isReadOnly ? 'default' : 'pointer' }}
                            dangerouslySetInnerHTML={{ __html: marked(item.title || '') }}
                        ></label>

                        {!isReadOnly && (
                            <span
                                id={`popover-checklist-item-more-${item._id}`}
                                onClick={(e) => handleItemMore(e, checklistId, item)}
                                data-popover-trigger
                            >
                                <MoreHoriz size={20} />
                            </span>
                        )}
                    </div>
                ) : (
                    <div className="checklist-footer-container">
                        <div className="add-item">
                            <div className={`textarea-wrapper ${isTextareaFocused ? 'focused' : ''}`}>
                                <textarea
                                    type="text"
                                    ref={checklistItemRef}
                                    placeholder="Add an item"
                                    value={editItemTitle}
                                    onChange={(e) => setEditItemTitle(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            updateItemTitle(checklistId, item._id, editItemTitle, item.checked);
                                        }
                                    }}
                                    rows={1}
                                    style={{ overflow: 'hidden' }}
                                    onFocus={() => setIsTextareaFocused(true)}
                                    onBlur={() => setIsTextareaFocused(false)}
                                />
                            </div>
                        </div>
                        <div className="checklist-footer">
                            <div className='left-button'>
                                <button className="add-btn" onClick={() => updateItemTitle(checklistId, item._id, editItemTitle, item.checked)}>Save</button>
                                <button className="cancel-btn" onClick={() => {
                                    setEditingItemId(null);
                                    setEditItemTitle('');
                                    setIsEditing(false);
                                }}>
                                    <Close size={18} />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const Checklists = ({ cardId, isReadOnly = false }) => {
    const dispatch = useDispatch();
    const [newItem, setNewItem] = useState('');
    const [editingItemId, setEditingItemId] = useState(null);
    const [editItemTitle, setEditItemTitle] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [hideCheckedItems, setHideCheckedItems] = useState(false); // Track whether to hide checked items
    const [isTextareaFocused, setIsTextareaFocused] = useState(false); // New focus state

    const [editingChecklistId, setEditingChecklistId] = useState(null);
    const [editChecklistTitle, setEditChecklistTitle] = useState('');
    const [localChecklistTitles, setLocalChecklistTitles] = useState({});
    const [isTitleInputFocused, setIsTitleInputFocused] = useState(false);




    const { handlePopoverClick } = useHandlePopoverClick();

    const checklistItemRef = useRef(null);

    // Per-item toggle sequence. Every check/uncheck click bumps the item's
    // counter; a request only "owns" the outcome if it is still the latest
    // click for that item when it resolves. This makes rapid toggling
    // latest-wins instead of bouncing through every in-flight response.
    const toggleSeqRef = useRef({});


    const { checklists, visibleAddNew, status } = useSelector((state) => state.checklists);
    const { card } = useSelector((state) => state.card);
    const { user } = useSelector((state) => state.user);



    useEffect(() => {
        dispatch(fetchChecklists(cardId));
    }, [cardId]);

    const updateChecklistItem = async ({ cardId, checklistId, itemId, title, checked }) => {
        try {

            const response = await fetch(`${config.API_URI}/v1/cards/${cardId}/checklists/${checklistId}/items/${itemId}`, {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',

                },
                credentials: 'include',
                body: JSON.stringify({ title, checked })
            });

            if (!response.ok) {
                throw new Error(`Error: ${response.statusText}`);
            }

            const data = await response.json();
            return data.checklists;
        } catch (error) {
            console.error('Error updating checklist item:', error.message);
            throw error;
        }
    };

    const updateChecklistTitle = async ({ cardId, checklistId, title }) => {
        try {


            const response = await fetch(`${config.API_URI}/v1/cards/${cardId}/checklists/${checklistId}`, {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ title })
            });



            if (!response.ok) throw new Error(`Error: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            console.error('Failed to update checklist title:', error.message);
            throw error;
        }
    };

    // Function to reorder checklist items
    const reorderChecklistItems = async ({ cardId, checklistId, items }) => {
        try {
            const response = await fetch(`${config.API_URI}/v1/cards/${cardId}/checklists/${checklistId}/reorder`, {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ items })
            });

            if (!response.ok) throw new Error(`Error: ${response.statusText}`);
            const data = await response.json();

            // Update with server response to ensure consistency
            if (data.checklists) {
                dispatch({
                    type: 'checklists/localUpdate',
                    payload: data.checklists
                });
            }


            return data;
        } catch (error) {
            console.error('Failed to reorder checklist items:', error.message);
            // Revert optimistic update on error
            dispatch(fetchChecklists(cardId));
            throw error;
        }
    };

    // Function to handle checklist item reordering
    const handleItemReorder = (checklistId, sourceIndex, targetIndex) => {
        const checklist = checklists.find(cl => cl._id === checklistId);
        if (!checklist) return;

        // Get all items (including filtered ones for proper indexing)
        const allItems = [...checklist.items];
        const visibleItems = allItems.filter(item => !hideCheckedItems || !item.checked);

        // Map visible indices to actual indices
        const sourceItem = visibleItems[sourceIndex];
        const targetItem = visibleItems[targetIndex];

        const actualSourceIndex = allItems.findIndex(item => item._id === sourceItem._id);
        const actualTargetIndex = allItems.findIndex(item => item._id === targetItem._id);

        // Reorder in the full items array
        const [movedItem] = allItems.splice(actualSourceIndex, 1);
        allItems.splice(actualTargetIndex, 0, movedItem);


        // ✅ Optimistic update
        dispatch(localReorderItems({
            checklistId,
            sourceIndex: actualSourceIndex,
            destinationIndex: actualTargetIndex
        }));

        // API call to persist the change
        reorderChecklistItems({
            cardId,
            checklistId,
            items: allItems.map(item => item._id)
        });
    };


    const handleCheck = async (checklistId, itemId, itemTitle) => {
        const checklist = checklists.find(cl => cl._id === checklistId);
        const item = checklist.items.find(i => i._id === itemId);
        const newCheckedState = !item.checked;

        // Stamp this click as the latest intent for the item. Out-of-order
        // responses below compare against this to decide if they still matter.
        const seq = (toggleSeqRef.current[itemId] || 0) + 1;
        toggleSeqRef.current[itemId] = seq;
        const isLatest = () => toggleSeqRef.current[itemId] === seq;

        // Toggle the UI immediately (optimistic) so the checkbox feels instant
        // and doesn't appear stuck while the request round-trips.
        dispatch(localUpdateItem({ checklistId, itemId, title: itemTitle, checked: newCheckedState }));
        // Lock this item so a stale socket echo / slow response can't flip the
        // box back while a request is in flight.
        dispatch(markItemToggling(itemId));

        try {
            // Actual API call
            await updateChecklistItem({
                cardId,
                checklistId,
                itemId: itemId,
                title: itemTitle,
                checked: newCheckedState
            });

            // A newer click superseded this one — ignore this stale response so
            // the box doesn't bounce back to an intermediate state.
            if (!isLatest()) return;

            if (card?.isActivityDetails && user?.user) {
                const isCompleted = newCheckedState;

                dispatch(addActivityOptimistically({
                    cardId,
                    userId: user.user._id,
                    actionType: isCompleted ? 'MARKED_CHECK_ITEM' : 'UNMARKED_CHECK_ITEM',
                    details: isCompleted
                        ? `completed <b>${itemTitle}</b> on this card.`
                        : `marked <b>${itemTitle}</b> as incomplete on this card.`,
                    user: user.user
                }));
            }

        } catch (error) {
            console.error('Failed to update check state:', error);
            // Only the latest request may revert — a superseded failure must not
            // clobber a newer optimistic state.
            if (isLatest()) {
                dispatch(localUpdateItem({ checklistId, itemId: itemId, title: itemTitle, checked: !newCheckedState }));
            }
        } finally {
            // Release the reconcile lock only when the latest request settles, so
            // earlier in-flight responses can't unlock the item prematurely.
            if (isLatest()) {
                dispatch(clearItemToggling(itemId));
            }
        }
    };


    const handleAddItem = async (checklistId) => {
        if (newItem.trim()) {
            const checklist = checklists.find(cl => cl._id === checklistId);
            const itemTitle = newItem;

            // Clear the input immediately so the next item can be typed
            // without waiting for the API round-trip. The optimistic localAddItem
            // below already shows the item in the list; clearing here just keeps
            // the input field aligned with the user's mental model.
            setNewItem('');
            dispatch(setVisibleAddNew(checklistId));

            // Stable temp id so the optimistic row keeps the same React key until
            // the server item replaces it — prevents the blink when adding fast.
            const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;

            try {
                dispatch(localAddItem({ checklistId, newItem: itemTitle, tempId }));

                await dispatch(addChecklistItem({ cardId, checklistId, newItem: itemTitle })).unwrap();

                // Add activity after successful creation if details are enabled
                if (card?.isActivityDetails && user?.user) {
                    dispatch(addActivityOptimistically({
                        cardId,
                        userId: user.user._id,
                        actionType: 'ADDED_CHECKLIST_ITEM',
                        details: `added <b>${itemTitle}</b> to <b>${checklist?.title || 'Checklist'}</b>.`,
                        user: user.user
                    }));
                }
            } catch (error) {
                console.error('Failed to add checklist item:', error);
                // Revert optimistic update (you might need a localDeleteItem and tempId for this)
                // dispatch(localDeleteItem({ checklistId, itemId: tempId }));
            }
        }
    };



    const updateItemTitle = (checklistId, itemId, title, checked) => {

        if (title) {
            updateChecklistItem({ cardId, checklistId, itemId, title, checked }).then(() => {
                // Now that server confirmed, update UI state
                dispatch(localUpdateItem({ checklistId, itemId, title, checked }));
            });
            setIsEditing(false);
            setEditingItemId(null);
            setEditItemTitle('');
        }
    };

    const handleDeleteChecklist = (e, checklist) => {
        handlePopoverClick(e, 'deleteAction', {
            type: 'CHECKLIST',
            id: checklist._id,
            cardId,
            checklistTitle: checklist.title
        });
    };

    const handleItemMore = (e, checklistId, item) => {
        handlePopoverClick(e, 'more', { type: 'CHECKLIST_ITEM_MORE', checklistId, item, cardId });
    }

    useEffect(() => {
        if (checklistItemRef.current) {
            checklistItemRef.current.style.height = '22px';
            checklistItemRef.current.style.height = `${checklistItemRef.current.scrollHeight}px`;
        }
    }, [editItemTitle, newItem]);

    useEffect(() => {
        if (checklistItemRef.current) {
            checklistItemRef.current.focus();
            checklistItemRef.current.select();
        }
    }, [visibleAddNew, editingItemId]);

    const progress = (items) => items?.length ? (items?.filter(item => item?.checked).length / items.length) * 100 : 0;

    if (status === 'loading') {
        return null;
    }


    return (
        checklists && (
            checklists.map(checklist => (
                <div className="zoobbe-checklist" key={checklist._id}>
                    <div className="checklist-header">

                        <h3 className="editable-checklist-title">
                            <div className='checklist-icon'>
                                <Checklist size={24} />
                            </div>
                            {editingChecklistId === checklist._id ? (
                                <input
                                    type="text"
                                    value={editChecklistTitle}
                                    autoFocus
                                    onChange={(e) => setEditChecklistTitle(e.target.value)}
                                    className={`checklist-title-input ${isTitleInputFocused ? 'focus' : ''}`}
                                    onFocus={() => setIsTitleInputFocused(true)}
                                    onBlur={async () => {
                                        const trimmed = editChecklistTitle.trim();
                                        const oldTitle = checklist.title;
                                        if (trimmed && trimmed !== oldTitle) {
                                            try {
                                                // 1. Update backend
                                                await updateChecklistTitle({
                                                    cardId,
                                                    checklistId: checklist._id,
                                                    title: trimmed
                                                });

                                                // 2. Now that server confirmed, update local title
                                                setLocalChecklistTitles(prev => ({
                                                    ...prev,
                                                    [checklist._id]: trimmed
                                                }));

                                                // 3. Add activity after successful update
                                                if (card?.isActivityDetails && user?.user) {
                                                    dispatch(addActivityOptimistically({
                                                        cardId,
                                                        userId: user.user._id,
                                                        actionType: 'UPDATED_CHECKLIST',
                                                        details: `renamed checklist from <b>${oldTitle}</b> to <b>${trimmed}</b>.`,
                                                        user: user.user
                                                    }));
                                                }
                                            } catch (error) {
                                                console.error('Failed to update checklist title:', error);
                                                // Revert local title on error
                                                setLocalChecklistTitles(prev => ({
                                                    ...prev,
                                                    [checklist._id]: oldTitle
                                                }));
                                            }
                                        }

                                        setEditingChecklistId(null);
                                    }}


                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            e.target.blur(); // Triggers `onBlur` to save
                                        }
                                    }}
                                />
                            ) : (
                                <span
                                    className="checklist-title-text"
                                    onClick={() => {
                                        setEditingChecklistId(checklist._id);
                                        setEditChecklistTitle(localChecklistTitles[checklist._id] || checklist.title);
                                    }}
                                >
                                    {localChecklistTitles[checklist._id] || checklist.title}
                                </span>
                            )}
                        </h3>

                        {editingChecklistId !== checklist._id && (
                            <div className="checklist-more-buttons">
                                <button
                                    className="hide-checked-item-btn"
                                    onClick={() => setHideCheckedItems(!hideCheckedItems)}
                                >
                                    {hideCheckedItems ? (
                                        <span className="material-symbols-outlined">radio_button_partial</span>
                                    ) : (
                                        <span className="material-symbols-outlined">radio_button_unchecked</span>
                                    )}
                                    {hideCheckedItems ? 'Show all' : 'Hide checked'}
                                </button>

                                {!isReadOnly && (
                                    <button
                                        className="checklist-delete-btn"
                                        onClick={(e) => handleDeleteChecklist(e, checklist)}
                                        data-popover-trigger
                                    >
                                        <Delete size={18} />
                                        Delete
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="progress-bar">
                        <span>{Math.round(progress(checklist.items))}%</span>
                        <div className='checklist-progress-bar'>
                            <div className="progress" style={{ width: `${progress(checklist.items)}%` }}></div>
                        </div>
                    </div>
                    <div className="checklist-items">
                        {checklist.items.filter(item => !hideCheckedItems || !item.checked).map((item, index) => (
                            item && item._id && (
                                <DraggableChecklistItem
                                    key={item._id}
                                    item={item}
                                    checklistId={checklist._id}
                                    index={index}
                                    editingItemId={editingItemId}
                                    editItemTitle={editItemTitle}
                                    isEditing={isEditing}
                                    isTextareaFocused={isTextareaFocused}
                                    handleCheck={handleCheck}
                                    handleItemMore={handleItemMore}
                                    setEditingItemId={setEditingItemId}
                                    setEditItemTitle={setEditItemTitle}
                                    setIsEditing={setIsEditing}
                                    setIsTextareaFocused={setIsTextareaFocused}
                                    updateItemTitle={updateItemTitle}
                                    checklistItemRef={checklistItemRef}
                                    onReorder={handleItemReorder}
                                    isReadOnly={isReadOnly}
                                />
                            )
                        ))}
                        {!isReadOnly && (
                            visibleAddNew === checklist._id ? (
                                <div className="checklist-footer-container">
                                    <div className="add-item">
                                        <div className={`textarea-wrapper ${isTextareaFocused ? 'focused' : ''}`}>

                                            <textarea
                                                type="text"
                                                ref={checklistItemRef}
                                                placeholder="Add an item"
                                                value={newItem}
                                                onChange={(e) => setNewItem(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleAddItem(checklist._id);
                                                    }
                                                }}
                                                rows={1}
                                                style={{ overflow: 'hidden' }}
                                                onFocus={() => setIsTextareaFocused(true)}  // Set focus state
                                                onBlur={() => setIsTextareaFocused(false)}   // Remove focus state
                                            />
                                        </div>
                                    </div>
                                    <div className="checklist-footer">
                                        <div className='left-button'>
                                            <button className="add-btn" onClick={() => handleAddItem(checklist._id)}>Add</button>
                                            <button className="cancel-btn" onClick={() => { setNewItem(''); dispatch(setVisibleAddNew(null)); }}>Discard</button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <button className="add-new-item-btn" onClick={() => dispatch(setVisibleAddNew(checklist._id))}>Add item</button>
                            )
                        )}
                    </div>
                </div >
            ))
        )
    );
};

export default Checklists;
